/**
 * /settings/impersonation — Sessões de depuração (spec 020, US3): quem viu o
 * portal como quem, quando e por quê. Últimos 90 dias, filtro por pessoa (quem
 * pediu ou quem foi visto) e Encerrar — inclusive a sessão de outro admin. A
 * linha nunca é apagada (FR-010): a API nem tem essa rota.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, ShieldAlert } from 'lucide-react';
import { IMPERSONATION_LIST_DAYS, type ImpersonationSession } from '@erp/shared';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, PageSpinner, Skeleton } from '@/components/ui/misc';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { formatDateTime } from '@/lib/utils';
import {
  IMPERSONATION_PAGE_SIZE,
  impersonationErrorMessage,
  impersonationSessionsQuery,
  useEndImpersonationSession,
} from './impersonation-queries';

/** "Chrome 131", "Firefox 132", "Safari"… — o texto inteiro fica no title */
function browserLabel(userAgent: string | null): string {
  if (!userAgent) return '—';
  const known: Array<[RegExp, string]> = [
    [/Edg\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of known) {
    const m = re.exec(userAgent);
    if (m) return `${name} ${m[1]}`;
  }
  return userAgent.slice(0, 24);
}

function StatusCell({ row }: { row: ImpersonationSession }) {
  if (row.status === 'active') {
    return (
      <div className="space-y-0.5">
        <Badge variant="success">Ativa</Badge>
        <div className="text-xs text-muted-foreground tabular-nums">até {formatDateTime(row.expiresAt)}</div>
      </div>
    );
  }
  if (row.status === 'ended') {
    return (
      <div className="space-y-0.5">
        <Badge variant="default">Encerrada</Badge>
        <div className="text-xs text-muted-foreground">
          <span className="tabular-nums">{formatDateTime(row.endedAt)}</span>
          {row.endedBy ? ` por ${row.endedBy.name}` : ''}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <Badge variant="warning">Expirada</Badge>
      <div className="text-xs text-muted-foreground tabular-nums">{formatDateTime(row.expiresAt)}</div>
    </div>
  );
}

export function ImpersonationSessionsPage() {
  const { can, ready } = usePermissions();
  if (!ready) return <PageSpinner />;
  if (!can('users.impersonate')) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          As sessões de depuração são vistas por quem tem a permissão “Ver como cliente” — por
          padrão, só administradores.
        </p>
      </div>
    );
  }
  return <SessionsList />;
}

function SessionsList() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const q = useDebouncedValue(search.trim(), 300);
  const list = useQuery(impersonationSessionsQuery({ q, page }));
  const end = useEndImpersonationSession();
  const total = list.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / IMPERSONATION_PAGE_SIZE));

  const confirmEnd = (row: ImpersonationSession) => {
    if (
      window.confirm(
        `Encerrar a sessão de ${row.actor.name} vendo como ${row.target.name}? O acesso deixa de valer na próxima requisição da aba.`
      )
    ) {
      end.mutate(row.id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/settings" className="transition-colors hover:text-foreground">
              Configurações
            </Link>
            <span className="mx-1.5">/</span>
            <span>Usuários</span>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">Sessões de depuração</span>
          </nav>
          <h1 className="mt-2 text-xl leading-7 font-semibold">Sessões de depuração</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Quem viu o portal como um usuário do cliente, quando e por quê — últimos{' '}
            {IMPERSONATION_LIST_DAYS} dias. As sessões ficam registradas e nunca são apagadas.
          </p>
        </div>
        <div className="relative">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Filtrar por pessoa (nome ou e-mail)"
            aria-label="Filtrar por pessoa"
            className="h-9 w-64 rounded-md border border-border bg-card pl-3 pr-8 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary sm:w-72"
          />
          <Search className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      {end.error ? (
        <p className="text-sm text-destructive" role="alert">
          {impersonationErrorMessage(end.error)}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {list.isPending ? (
          <div className="space-y-2 p-4" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState message={`Erro ao carregar as sessões: ${impersonationErrorMessage(list.error)}`} />
        ) : list.data.items.length === 0 ? (
          <EmptyState
            message={q ? `Nenhuma sessão com “${q}”` : 'Nenhuma sessão de depuração nos últimos 90 dias'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 w-36 px-4 font-semibold">Início</th>
                  <th className="h-9 px-4 font-semibold">Quem pediu</th>
                  <th className="h-9 px-4 font-semibold">Visto como</th>
                  <th className="h-9 min-w-56 px-4 font-semibold">Motivo</th>
                  <th className="h-9 w-40 px-4 font-semibold">Situação</th>
                  <th className="h-9 w-24 px-4 text-right font-semibold">Requisições</th>
                  <th className="h-9 w-40 px-4 font-semibold">Origem</th>
                  <th className="h-9 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((row) => (
                  <tr key={row.id} className="border-b border-divider align-top last:border-0 hover:bg-accent/60">
                    <td className="px-4 py-2.5 whitespace-nowrap text-[13px] tabular-nums">
                      {formatDateTime(row.startedAt)}
                    </td>
                    <td className="max-w-48 px-4 py-2.5">
                      <div className="truncate font-medium">{row.actor.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{row.actor.email}</div>
                    </td>
                    <td className="max-w-56 px-4 py-2.5">
                      <div className="truncate font-medium">{row.target.name}</div>
                      <div
                        className="truncate text-xs text-muted-foreground"
                        title={[...row.target.clients, row.target.email].join(' · ')}
                      >
                        {row.target.clients.length > 0 ? row.target.clients.join(', ') : row.target.email}
                      </div>
                    </td>
                    <td className="min-w-56 max-w-96 px-4 py-2.5 text-[13px] break-words">{row.reason}</td>
                    <td className="px-4 py-2.5">
                      <StatusCell row={row} />
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.requestCount}</td>
                    <td
                      className="px-4 py-2.5 text-xs whitespace-nowrap text-muted-foreground"
                      title={row.userAgent ?? undefined}
                    >
                      <div className="tabular-nums">{row.ip ?? '—'}</div>
                      <div>{browserLabel(row.userAgent)}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {row.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="xs"
                          disabled={end.isPending && end.variables === row.id}
                          onClick={() => confirmEnd(row)}
                        >
                          Encerrar
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isPending && !list.isError && total > IMPERSONATION_PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t px-4 py-2.5">
            <span className="text-[13px] text-muted-foreground tabular-nums">
              {page * IMPERSONATION_PAGE_SIZE + 1}–{Math.min((page + 1) * IMPERSONATION_PAGE_SIZE, total)} de{' '}
              {total}
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="size-4" aria-hidden />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
                <ChevronRight className="size-4" aria-hidden />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
